import { useEffect, useCallback } from 'react'
import { KYCStatus } from '@/common/types'
import { KYC_STATUS } from '@/common/constants'
import useKycStore from '@/kyc/kycStore'

const GRACE_PERIOD_MS = 180000 // 3 minutes

/**
 * Manages KYC completion grace period to handle backend sync delay.
 *
 * After user completes KYC:
 * 1. Enters 3-minute grace period (hides all resume prompts)
 * 2. Automatically clears when backend confirms success/failure
 * 3. Automatically expires after 3 minutes
 *
 * Why: External KYC services have sync delay. This prevents showing
 * incorrect "resume" prompts immediately after user completes KYC.
 *
 * @param kyc - Current KYC status from API
 *
 * @example
 * ```tsx
 * function Dashboard() {
 *   const { data: kyc } = useFetchKYC(activeAccountId)
 *   const { isInGracePeriod } = useKycCompletionState(kyc)
 *
 *   const shouldShowResume = !isInGracePeriod && kyc?.canInitiateKyc
 *   return shouldShowResume ? <ResumeCard /> : null
 * }
 * ```
 */
export function useKycCompletionState(kyc: KYCStatus | undefined) {
    const { completionTimestamp, setCompletionTimestamp } = useKycStore()

    const markKycCompleted = useCallback(() => {
        setCompletionTimestamp(Date.now())
    }, [setCompletionTimestamp])

    // Clear when KYC reaches terminal state
    useEffect(() => {
        if (!kyc) return
        if (kyc.status === KYC_STATUS.VERIFICATION_SUCCESS || kyc.status === KYC_STATUS.VERIFICATION_FAILED) {
            setCompletionTimestamp(null)
        }
    }, [kyc, setCompletionTimestamp])

    // Compute grace period state (pure calculation, no side effects)
    const isInGracePeriod = completionTimestamp !== null && Date.now() - completionTimestamp < GRACE_PERIOD_MS

    // Auto-clear timestamp when grace period expires
    useEffect(() => {
        if (!completionTimestamp) return

        const remaining = GRACE_PERIOD_MS - (Date.now() - completionTimestamp)
        if (remaining <= 0) {
            setCompletionTimestamp(null)
            return
        }

        // Set timer to clear when grace period expires
        const timer = setTimeout(() => {
            setCompletionTimestamp(null)
        }, remaining)

        return () => clearTimeout(timer)
    }, [completionTimestamp, setCompletionTimestamp])

    return {
        isInGracePeriod,
        markKycCompleted,
    }
}
